import type { E_SnapTradeBrokerage } from "@lattice/core-lib/types/snaptrade/brokerage.types";
import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, fromPromise, setup } from "xstate";

export type T_WebBrokerage = {
  code: E_SnapTradeBrokerage;
  featured: boolean;
  logoUrl: string;
  name: string;
};

export type T_WebBrokerageConnectInput = {
  generateLoginLink: (brokerage: T_WebBrokerage) => Promise<string>;
  onConnected: (brokerage: T_WebBrokerage) => void;
  startOpen: boolean;
  syncConnection: (
    authorizationId: string,
    brokerage: T_WebBrokerage,
  ) => Promise<{ success: true }>;
};

type T_WebBrokerageConnectContext = {
  authorizationId: string | null;
  error: string | null;
  input: T_WebBrokerageConnectInput;
  loginLink: string | null;
  selectedBrokerage: T_WebBrokerage | null;
};

type T_WebBrokerageConnectEvent =
  | { type: "OPEN_CONNECT" }
  | { type: "CLOSE_CONNECT" }
  | { brokerage: T_WebBrokerage; type: "SELECT_BROKERAGE" }
  | { authorizationId: string; type: "AUTHORIZED" }
  | { message: string; type: "PORTAL_ERROR" }
  | { type: "PORTAL_CANCELLED" }
  | { type: "RETRY" };

type T_LoginLinkActorInput = {
  brokerage: T_WebBrokerage | null;
  generateLoginLink: T_WebBrokerageConnectInput["generateLoginLink"];
};

type T_SyncActorInput = {
  authorizationId: string | null;
  brokerage: T_WebBrokerage | null;
  syncConnection: T_WebBrokerageConnectInput["syncConnection"];
};

const types: SetupTypes<
  T_WebBrokerageConnectContext,
  T_WebBrokerageConnectEvent,
  Record<never, string>,
  string,
  T_WebBrokerageConnectInput,
  unknown,
  EventObject,
  MetaObject
> = {};

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Could not connect this brokerage. Please try again.";
}

export const webBrokerageConnectMachine = setup({
  types,
  guards: {
    startsOpen: ({ context }) => context.input.startOpen,
  },
  actors: {
    generateLoginLink: fromPromise<string, T_LoginLinkActorInput>(
      async ({ input }) => {
        if (input.brokerage === null) {
          throw new Error("Choose a brokerage first.");
        }
        return input.generateLoginLink(input.brokerage);
      },
    ),
    syncConnection: fromPromise<{ success: true }, T_SyncActorInput>(
      async ({ input }) => {
        if (input.authorizationId === null || input.brokerage === null) {
          throw new Error("Brokerage authorization was incomplete.");
        }
        return input.syncConnection(input.authorizationId, input.brokerage);
      },
    ),
  },
  actions: {
    clear: assign({
      authorizationId: null,
      error: null,
      loginLink: null,
      selectedBrokerage: null,
    }),
    notifyConnected: ({ context }) => {
      if (context.selectedBrokerage) {
        context.input.onConnected(context.selectedBrokerage);
      }
    },
    selectBrokerage: assign({
      error: null,
      selectedBrokerage: ({ event }) =>
        event.type === "SELECT_BROKERAGE" ? event.brokerage : null,
    }),
    setAuthorization: assign({
      authorizationId: ({ event }) =>
        event.type === "AUTHORIZED" ? event.authorizationId : null,
      error: null,
    }),
    setError: assign({
      error: (_, params: { error: unknown }) => getErrorMessage(params.error),
    }),
    setLoginLink: assign({
      loginLink: (_, params: { loginLink: string }) => params.loginLink,
    }),
    setPortalError: assign({
      error: ({ event }) =>
        event.type === "PORTAL_ERROR"
          ? event.message
          : "The brokerage portal could not finish.",
    }),
  },
}).createMachine({
  id: "webBrokerageConnect",
  context: ({ input }) => ({
    authorizationId: null,
    error: null,
    input,
    loginLink: null,
    selectedBrokerage: null,
  }),
  initial: "starting",
  states: {
    starting: {
      always: [
        { guard: "startsOpen", target: "selecting" },
        { target: "idle" },
      ],
    },
    idle: { on: { OPEN_CONNECT: "selecting" } },
    selecting: {
      on: {
        CLOSE_CONNECT: { actions: "clear", target: "idle" },
        SELECT_BROKERAGE: {
          actions: "selectBrokerage",
          target: "generatingLink",
        },
      },
    },
    generatingLink: {
      invoke: {
        src: "generateLoginLink",
        input: ({ context }) => ({
          brokerage: context.selectedBrokerage,
          generateLoginLink: context.input.generateLoginLink,
        }),
        onDone: {
          actions: {
            type: "setLoginLink",
            params: ({ event }) => ({ loginLink: event.output }),
          },
          target: "authorizing",
        },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "failed",
        },
      },
      on: { CLOSE_CONNECT: { actions: "clear", target: "idle" } },
    },
    authorizing: {
      on: {
        AUTHORIZED: { actions: "setAuthorization", target: "syncing" },
        PORTAL_CANCELLED: { actions: "clear", target: "idle" },
        PORTAL_ERROR: { actions: "setPortalError", target: "failed" },
      },
    },
    syncing: {
      invoke: {
        src: "syncConnection",
        input: ({ context }) => ({
          authorizationId: context.authorizationId,
          brokerage: context.selectedBrokerage,
          syncConnection: context.input.syncConnection,
        }),
        onDone: { actions: "notifyConnected", target: "connected" },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "failed",
        },
      },
    },
    connected: {
      on: { CLOSE_CONNECT: { actions: "clear", target: "idle" } },
    },
    failed: {
      on: {
        CLOSE_CONNECT: { actions: "clear", target: "idle" },
        RETRY: { actions: "clear", target: "selecting" },
      },
    },
  },
});
